/**
 * Map Service using free OpenStreetMap APIs via Cloudflare Pages proxy.
 *
 * When properties have pre-defined nearby places data (from mock data),
 * those are used directly. When they don't (e.g. from Google Drive),
 * the Overpass API proxy fetches nearby places dynamically.
 *
 * - /api/geocode -> Nominatim (forward geocoding)
 * - /api/nearby  -> Overpass API (POI search)
 *
 * Completely free, no API key or billing required.
 *
 * Geocoding Strategy (priority order):
 * 1. additionalAddress[0] (additionalAddress1)
 * 2. additionalAddress[1] (additionalAddress2)
 * 3. displayAddress
 * 4. district + city
 *
 * Server-side fallback:
 *   The /api/geocode endpoint automatically retries with ", Vietnam" or
 *   ", Ho Chi Minh City, Vietnam" if the original query returns empty.
 *   It also handles Nominatim rate limiting (1 req/sec) server-side.
 *   Therefore the client only sends a single query per address.
 *
 * Encryption:
 *   The property details page (HousingDetail) requests encrypted geocode
 *   responses via ?encrypted=1, mirroring the drive-all-properties pattern.
 *   The server wraps the body as { encrypted: "<base64>" }, which is decrypted
 *   here using the seed key. All other pages (home, search, admin) keep
 *   receiving plain JSON for backward compatibility.
 */

import config from '../config';
import { encrypt, decrypt } from '../utils/crypto';

/**
 * Geocode a location query via Cloudflare proxy.
 *
 * The server-side /api/geocode endpoint handles:
 * - Rate limiting (serializes Nominatim requests with 1.1s gaps)
 * - Auto-fallback with ", Vietnam" / ", Ho Chi Minh City, Vietnam" suffixes
 * - D1 caching of successful results (keyed by the ORIGINAL query)
 *
 * Encryption option:
 * When `options.encrypted` is true, the request adds ?encrypted=1 so the server
 * wraps the JSON body as { encrypted: "<base64>" } (same XOR + base64 pattern as
 * /api/drive-all-properties). The response is decrypted here with the seed key.
 * Unencrypted calls (admin pages, search pages, home page) keep plain JSON.
 *
 * @param {string} queryStr - The location string to geocode
 * @param {string|object} [_fallbackCity] - DEPRECATED string param (server handles fallback),
 *   or an options object { encrypted: boolean } for the property details page.
 * @returns {Promise<{lat: number, lng: number, display_name: string} | null>}
 */
export async function geocodeLocation(queryStr, _fallbackCity = '') {
  try {
    if (!queryStr || !queryStr.trim()) return null;

    // Allow second arg to be an options object for the property details page.
    // Legacy string usage (the deprecated _fallbackCity) is still supported.
    const options = typeof _fallbackCity === 'object' && _fallbackCity !== null
      ? _fallbackCity
      : {};
    const encrypted = !!options.encrypted;

    // In the encrypted exchange, the `q` query param itself is XOR+base64
    // encrypted (same pattern as drive-all-properties) so the address is never
    // visible as plaintext in the URL/Network tab. The server decrypts it with
    // the matching seed key. Non-encrypted calls (admin, search, home) keep a
    // plaintext query for backward compatibility.
    const url = encrypted
      ? `/api/geocode?q=${encodeURIComponent(encrypt(queryStr, config.seedKey))}&encrypted=1`
      : `/api/geocode?q=${encodeURIComponent(queryStr)}`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const body = await response.json();

    // If the response is encrypted ({ encrypted: "<base64>" }), decrypt it first.
    let data = body;
    if (encrypted && body && body.encrypted) {
      const seedKey = config.seedKey;
      const decryptedJson = decrypt(body.encrypted, seedKey);
      try {
        data = JSON.parse(decryptedJson);
      } catch (parseErr) {
        console.warn('[mapService] Failed to parse decrypted geocode response:', parseErr.message);
        return null;
      }
    }

    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        display_name: data[0].display_name || '',
      };
    }

    return null;
  } catch (err) {
    console.warn('[mapService] Geocode error:', err.message);
    return null;
  }
}

/**
 * Search for nearby places using Overpass API via Cloudflare proxy.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<Array<{name: string, nameVi: string, lat: number, lng: number, type: string}>>}
 */
export async function searchNearbyPlaces(lat, lng) {
  try {
    const response = await fetch('/api/nearby', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lng }),
    });

    if (!response.ok) return [];

    const data = await response.json();
    const elements = data.elements || [];

    const typeMap = {
      university: 'university',
      hospital: 'hospital',
      market: 'market',
      mall: 'mall',
    };

    const seen = new Set();
    const places = [];

    for (const el of elements) {
      const tags = el.tags || {};
      const type = typeMap[tags.amenity] || typeMap[tags.shop];
      if (!type) continue;

      const name = tags.name || tags['name:vi'] || tags['name:en'] || '';
      if (!name) continue;

      const key = name.toLowerCase().trim();
      if (seen.has(key)) continue;
      seen.add(key);

      const placeLat = parseFloat(el.lat ?? el.center?.lat);
      const placeLng = parseFloat(el.lon ?? el.center?.lon);
      if (isNaN(placeLat) || isNaN(placeLng)) continue;

      places.push({
        name,
        nameVi: tags['name:vi'] || name,
        lat: placeLat,
        lng: placeLng,
        type,
      });

      if (places.length >= 10) break;
    }

    // Keep at most 2 of each type
    const typeOrder = ['university', 'hospital', 'market', 'mall'];
    const sorted = [];
    for (const t of typeOrder) {
      const filtered = places.filter((p) => p.type === t).slice(0, 2);
      sorted.push(...filtered);
    }

    return sorted;
  } catch (err) {
    console.warn('[mapService] Nearby search error:', err.message);
    return [];
  }
}

/**
 * Fetch map data for a property using the correct geocoding priority:
 * 1. additionalAddress[0]
 * 2. additionalAddress[1]
 * 3. displayAddress (the `address` param)
 * 4. district + city
 *
 * When `opts.encrypted` is true (property details page), all geocode calls go
 * through the encrypted /api/geocode?encrypted=1 exchange. Other pages consume
 * plain JSON.
 *
 * @param {string} address - The display address to geocode (fallback #3)
 * @param {string} district - District name (fallback #4)
 * @param {string} city - City name (fallback #4)
 * @param {Array<string>} [additionalAddresses] - Array of up to 2 additional addresses
 * @param {object} [opts] - Options { encrypted: boolean }
 * @returns {Promise<{ coordinates: {lat, lng} | null, places: Array }>}
 */
export async function fetchMapData(address, district, city, additionalAddresses = [], opts = {}) {
  // Property details page passes { encrypted: true } so geocode responses are encrypted.
  const geocodeOptions = opts.encrypted ? { encrypted: true } : {};
  let coordinates = null;

  // Step 1: Try each additional address in order (additionalAddress[0], then additionalAddress[1])
  if (additionalAddresses && additionalAddresses.length > 0) {
    for (const addr of additionalAddresses) {
      if (!addr || !addr.trim()) continue;

      coordinates = await geocodeLocation(addr.trim(), geocodeOptions);
      if (coordinates) {
        break;
      }
    }
  }

  // Step 2: Fallback to displayAddress
  if (!coordinates && address) {
    coordinates = await geocodeLocation(address, geocodeOptions);
  }

  // Step 3: Fallback to district + city
  if (!coordinates) {
    coordinates = await geocodeLocation(district, geocodeOptions);
  }

  let places = [];

  // Always dynamically fetch nearby places via Overpass (no hardcoded pins)
  if (coordinates) {
    places = await searchNearbyPlaces(coordinates.lat, coordinates.lng);
  }

  return { coordinates, places };
}

/**
 * Concurrency limiter: runs async functions with a max concurrency.
 */
class ConcurrencyLimiter {
  constructor(maxConcurrent) {
    this.maxConcurrent = maxConcurrent;
    this.active = 0;
    this.queue = [];
  }

  async run(fn) {
    if (this.active >= this.maxConcurrent) {
      await new Promise(resolve => this.queue.push(resolve));
    }
    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next();
      }
    }
  }
}

/**
 * In-memory cache for geocoded addresses to avoid redundant API calls.
 * Shared across all consumers (UniversitySearchPage, AddressSearchPage, etc.).
 * Can be pre-populated from D1 via syncGeocodeCacheFromD1().
 */
const geocodeCache = {};

/**
 * Export the geocodeCache for direct inspection (e.g. by sync function).
 * @returns {object} The geocode cache object (query -> { lat, lng })
 */
export function getGeocodeCache() {
  return geocodeCache;
}

/**
 * Clear the local in-memory geocode cache entirely.
 * This forces the next geocoding round to fetch from the server/D1 again.
 */
export function clearGeocodeCache() {
  const count = Object.keys(geocodeCache).length;
  for (const key in geocodeCache) {
    delete geocodeCache[key];
  }
  console.log(`[Geocode Cache] Cleared ${count} entries from local cache`);
  return count;
}

/**
 * Clear the server-side in-memory geocode cache (inside the Cloudflare Worker).
 * Also clears ALL entries from the D1 geocode_cache table.
 *
 * Call this together with clearGeocodeCache() for a full cache reset.
 *
 * POST /api/geocode-clear-cache
 *
 * @returns {Promise<{ success: boolean, memoryCleared: number, d1Cleared: number, error?: string }>}
 */
export async function clearServerGeocodeCache() {
  try {
    const response = await fetch('/api/geocode-clear-cache', {
      method: 'POST',
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    console.log(`[Geocode Clear] Server cache cleared: ${data.memoryCleared} in-memory, ${data.d1Cleared} from D1`);
    return { success: true, memoryCleared: data.memoryCleared || 0, d1Cleared: data.d1Cleared || 0 };
  } catch (err) {
    console.warn('[Geocode Clear] Failed to clear server cache:', err.message);
    return { success: false, memoryCleared: 0, d1Cleared: 0, error: err.message };
  }
}

/**
 * Push the local in-memory geocode cache TO D1 persistent storage.
 *
 * Sends ALL current entries from the local geocodeCache to the server endpoint
 * POST /api/geocode-sync, which stores them in D1. This makes the cached results
 * available to other users/sessions who later call syncGeocodeCacheFromD1().
 *
 * @returns {Promise<{ success: boolean, count: number, error?: string }>}
 */
export async function syncGeocodeCacheToD1() {
  const entries = [];
  for (const [query, coords] of Object.entries(geocodeCache)) {
    entries.push({
      query,
      lat: coords.lat,
      lng: coords.lng,
      display_name: coords.display_name || '',
    });
  }

  if (entries.length === 0) {
    return { success: true, count: 0 };
  }

  try {
    const response = await fetch('/api/geocode-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    console.log(`[Geocode Sync] Pushed ${data.stored} entries from local cache to D1`);
    return { success: true, count: data.stored || 0 };
  } catch (err) {
    console.warn('[Geocode Sync] Failed to push to D1:', err.message);
    return { success: false, count: 0, error: err.message };
  }
}

/**
 * Sync the local in-memory geocode cache from D1 persistent storage.
 *
 * Fetches ALL cached geocode entries from the server endpoint /api/geocode-sync
 * and populates the local geocodeCache so subsequent property geocoding
 * is instant — no Nominatim calls needed.
 *
 * Shows a status banner while syncing.
 *
 * @returns {Promise<{ success: boolean, count: number, error?: string }>}
 */
export async function syncGeocodeCacheFromD1() {
  try {
    const response = await fetch('/api/geocode-sync', {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    if (!data.entries || data.entries.length === 0) {
      return { success: true, count: 0 };
    }

    // Populate the in-memory cache from D1 entries
    let populatedCount = 0;
    for (const entry of data.entries) {
      if (entry.query && entry.lat != null && entry.lng != null) {
        geocodeCache[entry.query] = { lat: entry.lat, lng: entry.lng, display_name: entry.display_name || '' };
        populatedCount++;
      }
    }

    console.log(`[Geocode Sync] Populated ${populatedCount} entries into local geocode cache from D1`);
    return { success: true, count: populatedCount };
  } catch (err) {
    console.warn('[Geocode Sync] Failed to sync from D1:', err.message);
    return { success: false, count: 0, error: err.message };
  }
}

/**
 * Build a fallback geocoding query from displayAddress -> district+city.
 * NOTE: This does NOT include additionalAddresses - those are tried FIRST
 * by the caller in geocodePropertiesParallel's loop before this is called.
 */
function buildAddressQuery(property) {
  const district = property.districtVi || property.district || '';
  const city = property.cityVi || property.city || '';
  // Use displayAddress, then fallback to old address field
  const displayAddr = property.displayAddressVi || property.displayAddress || property.addressVi || property.address || '';
  if (displayAddr && district) return `${displayAddr}, ${district}, ${city || 'Ho Chi Minh City'}`;
  if (displayAddr) return `${displayAddr}, ${city || 'Ho Chi Minh City'}`;
  if (district) return `${district}, ${city || 'Ho Chi Minh City'}`;
  return null;
}

/**
 * Geocode multiple properties in parallel with concurrency control.
 * Uses the following priority for each property:
 * 1. additionalAddress[0]
 * 2. additionalAddress[1]
 * 3. displayAddress -> district -> city (via buildAddressQuery)
 *
 * IMPORTANT: The server-side /api/geocode endpoint handles:
 * - Nominatim rate limiting (serialized queue with 1.1s gaps)
 * - Auto-fallback with ", Vietnam" / ", Ho Chi Minh City, Vietnam"
 * - D1 caching of results keyed by the original query
 *
 * So this client-side function only sends 1 query per address (no city suffix variants),
 * and the server takes care of the rest.
 *
 * @param {Array} properties - Array of property objects
 * @param {number} _concurrency - DEPRECATED (server handles rate limiting). Kept for backward compat.
 * @param {Function} onProgress - Optional callback(done, total) called as each completes
 * @returns {Promise<Array>} Properties enriched with lat/lng, _geocodeFailed flags
 */
export async function geocodePropertiesParallel(properties, _concurrency = 5, onProgress = null) {
  if (!properties || properties.length === 0) return [];

  const limiter = new ConcurrencyLimiter(50); // High concurrency is fine — server serializes requests

  // Build all tasks
  const tasks = properties.map((prop) => async () => {
    const additionalAddresses = prop.additionalAddresses || [];
    const fallbackQuery = buildAddressQuery(prop);

    // If no address available at all
    if (!fallbackQuery && additionalAddresses.length === 0) {
      return { ...prop, lat: null, lng: null, _geocodeFailed: true, _geocodeAddress: '' };
    }

    let coords = null;
    let usedAddress = '';

    // Step 1: Try each additional address one by one
    if (additionalAddresses.length > 0) {
      for (const addr of additionalAddresses) {
        if (!addr || !addr.trim()) continue;
        const cacheKey = addr.toLowerCase().trim();
        coords = geocodeCache[cacheKey];
        if (!coords) {
          try {
            // Send only the address — the server handles fallback with ", Vietnam" etc.
            coords = await geocodeLocation(addr);
            if (coords) {
              geocodeCache[cacheKey] = coords;
              usedAddress = addr;
            }
          } catch {
            coords = null;
          }
        } else {
          usedAddress = addr;
        }
        if (coords) break;
      }
    }

    // Step 2: Fallback to displayAddress -> district -> city (via buildAddressQuery)
    if (!coords && fallbackQuery) {
      const cacheKey = fallbackQuery.toLowerCase().trim();
      coords = geocodeCache[cacheKey];
      if (!coords) {
        try {
          // Send only the fallback query — the server handles fallback with ", Vietnam" etc.
          coords = await geocodeLocation(fallbackQuery);
          if (coords) {
            geocodeCache[cacheKey] = coords;
          }
        } catch {
          coords = null;
        }
      }
    }

    return {
      ...prop,
      lat: coords?.lat || null,
      lng: coords?.lng || null,
      _geocodeFailed: !coords,
      _geocodeAddress: usedAddress || fallbackQuery || (additionalAddresses.length > 0 ? additionalAddresses[0] : ''),
    };
  });

  // Run all tasks with concurrency control
  let doneCount = 0;
  const total = tasks.length;

  const taskPromises = tasks.map((task) =>
    limiter.run(async () => {
      const result = await task();
      doneCount++;
      if (onProgress) {
        onProgress(doneCount, total);
      }
      return result;
    })
  );

  const enrichedList = await Promise.all(taskPromises);

  return enrichedList;
}